import {once} from 'redux-when';
import sjcl from 'sjcl';
import * as request from "superagent";

import {
  RC_CHECK_STATE,
  RC_CONNECT,
  RC_DISCONNECT,
  RC_MESSAGE_DIRECT,
  RC_METHOD,
  RC_NOTIFICATION_CHECK,
  RC_REGISTER,
  RC_ROOM,
  RC_ROOM_REMOVE,
  RC_SUBSCRIBE,
  RC_TYPING_SEND,
  RC_MESSAGE_ALL
} from "../constants/actionTypes";
import {ROCKET_CHAT_DOMAIN, USER_SECRET_URL, ROCKET_CHAT_LOGIN_URL, ROCKET_CHAT_HISTORY_URL} from "../constants/api";
import {
  TW_ACTION_CANCEL,
  TW_ACTION_DECLINE,
  TW_ACTION_SEPARATOR,
  TW_ACTION_STOP,
  TW_VIDEO_TAG
} from "../../twilio/constants";
import {MESSAGE_TYPING_WAIT} from "../../../../screens/message/constants";
import {
  RC_CHECK_STATE_STEP,
  RC_MESSAGE_NUMBER,
  RC_METHOD_DIRECT_MESSAGE,
  RC_METHOD_HISTORY,
  RC_METHOD_LOGIN,
  RC_METHOD_READ_MESSAGES,
  RC_METHOD_SEND_MESSAGE,
  RC_METHOD_SUBSCRIPTIONS,
  RC_METHOD_USER_ROLES,
  RC_NOTIFY_ROOM,
  RC_NOTIFY_ROOM_MESSAGE,
  RC_NOTIFY_USER,
  RC_SUBSCRIPTION_MESSAGE,
  RC_SUBSCRIPTION_NOTIFICATION,
  RC_SUBSCRIPTION_TYPING,
  RC_USER_PREFIX_FP,
  RC_USER_PREFIX_T
} from "../constants";
import * as auth from "../../../actions/auth";
import {getSenior} from "../../../../screens/members/actions/seniorsAction";
import {getRelative} from "../../../../screens/members/actions/relativesAction";
import {addNotification} from "../../notification/actions/notificationsAction";
import {NOTIFICATION_WARNING} from "../../notification/constants";
import {requestAuth} from "../../../../request";
import { PROFILE_URL } from '../../../../screens/user/constants/api';

let isCheckState = false;

/**
 * Gets a list of stored message
 * Returns empty array in case there is no stored message
 * @returns {array} list of stored messages
 */
export function getStoredMessages() {
  return JSON.parse(localStorage.messages || "[]");
}

/**
 * Checks whether room is senior or not
 * @param {string} roomId - room identification
 * @returns {boolean} is senior or not
 */
export function isSenior(roomId) {
  return roomId.startsWith(RC_USER_PREFIX_T);
}

/**
 * Checks whether room is relative or not
 * @param {string} roomId - room identification
 * @returns {boolean} is relative or not
 */
export function isRelative(roomId) {
  return roomId.startsWith(RC_USER_PREFIX_FP);
}

/**
 * Gets senior id from room id
 * @param {string} roomId - room identification
 * @returns {string} senior id
 */
export function getSeniorId(roomId) {
  return roomId.replace(RC_USER_PREFIX_T, "").split('_')[0];
}

/**
 * Gets relative id from room id
 * @param {string} roomId - room identification
 * @returns {string} relative id
 */
export function getRelativeId(roomId) {
  return roomId.replace(RC_USER_PREFIX_FP, "").split('_')[0];
}

/**
 * Gets sender
 * @param {string} roomId - room identification
 * @returns {function(dispatch): {object}} sender
 */
export function getSender(roomId) {
  return (dispatch) => {
    let user = {};

    if (isSenior(roomId)) {
      user = dispatch(
        getSenior(
          getSeniorId(
            roomId
          )
        )
      );
    } else if (isRelative(roomId)) {
      user = dispatch(
        getRelative(
          getRelativeId(
            roomId
          )
        )
      );
    } else {
      throw new Error('Unknown room specification');
    }

    return user;
  }
}

/**
 * Subscribes for events
 * @param {string} name - sub name
 * @param {string} event - sub event
 * @returns {object} subscription action
 */
export function subscribe(name, event = '') {
  return (dispatch) => {
    dispatch(
      {
        type: RC_SUBSCRIBE,
        payload: {
          name: name,
          event: event
        }
      }
    );
  }
}

/**
 * Subscribes user to a room events
 * @param {string} rid - room identification
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function onRoom(rid) {
  return (dispatch) => {
    dispatch(subscribe(RC_NOTIFY_ROOM_MESSAGE, rid));
    dispatch(subscribe(RC_NOTIFY_ROOM, `${rid}${RC_SUBSCRIPTION_TYPING}`));
  }
}

/**
 * Gets a secret
 * @returns {Promise<string>} a promise with secret
 */
export function getSecret() {
  return new Promise((resolve, reject) => {
    requestAuth()
      .get(USER_SECRET_URL)
      .then((response) => {
        resolve(response.body.secret);
      })
      .catch(reject);
  });
}

/**
 * Subscribes to notifications for current user
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function subscribeUserNotification() {
  return (dispatch, getState) => {
    dispatch(
      subscribe(
        RC_NOTIFY_USER,
        `${getState().rocketChat.user.id}${RC_SUBSCRIPTION_NOTIFICATION}`
      )
    );
  };
}

/**
 * Subscribes to current user changes
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function subscribeUserData() {
  return (dispatch) => {
    dispatch(subscribe('userData'));
  };
}

/**
 * Subscribes to other users changes
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function subscribeActiveUsers() {
  return (dispatch) => {
    dispatch(subscribe('activeUsers'));
  };
}

/**
 * Subscribes to other users changes
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function subscribeMessage() {
  return (dispatch, getState) => {
    dispatch(
      subscribe(
        RC_NOTIFY_USER,
        `${getState().rocketChat.user.id}${RC_SUBSCRIPTION_MESSAGE}`
      )
    );
  };
}

/**
 * Connects to Rocket.Chat
 * @see login Maybe you don`t need to call it directly
 * @param {string} token auth token
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function connect(token) {
  return (dispatch) => {
    return Promise.resolve(
      dispatch(
        {
          type: RC_CONNECT,
          domain: ROCKET_CHAT_DOMAIN,
          token: token
        }
      )
    );
  }
}

/**
 * Creates direct message room
 * @param {string} user - username
 * @returns {function(dispatch, getState): Promise<object>} promised thunk to be dispatched
 */
export function createDirectMessage(user) {
  return (dispatch, getState) => {
    dispatch(
      {
        type: RC_ROOM,
        roomName: user
      }
    );
    dispatch(
      {
        type: RC_METHOD,
        payload: {
          method: RC_METHOD_DIRECT_MESSAGE,
          params: [user]
        }
      }
    );
    return new Promise((resolve) => {
      dispatch(
        once(
          (state) => {
            return user in state.rocketChat.rooms;
          },
          () => {
            resolve(getState().rocketChat.rooms[user]);
            return {type: RC_MESSAGE_DIRECT};
          }
        )
      );
    });
  };
}

/**
 * Checks whether a room is a message subscription
 * @param {string} rid - room identification
 * @returns {function(dispatch, getState): boolean} return a thunk to be dispatched
 */
export function hasRoomMessageSubscription(rid) {
  return (dispatch, getState) => {
    return getState().rocketChat.subscriptions.reduce((state, subscription) => {
      return state
        ? state
        : subscription === `${RC_NOTIFY_ROOM_MESSAGE} ${rid}`;
    }, false);
  }
}

/**
 * Sends a message to a room
 * @param {string} rid - room identification
 * @param {string} message - direct message
 * @returns {function(dispatch, getState)} return a thunk to be dispatched
 */
export function sendMessage(rid, message) {
  return (dispatch) => {
    const param = {
      rid: rid,
      msg: message
    };

    if (dispatch(hasRoomMessageSubscription(rid))) {
      localStorage.messages = JSON.stringify(
        [
          ...getStoredMessages(),
          param
        ]
      );
    }
    dispatch(
      {
        type: RC_METHOD,
        payload: {
          method: RC_METHOD_SEND_MESSAGE,
          params: [param]
        }
      }
    );
  };
}

/**
 * Gets user`s room for direct messages
 * @param {string} user - username
 * @returns {function(dispatch, getState): Promise<string>} promised thunk to be dispatched for getting room identification
 */
export function getUserRoom(user) {
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      if (user in getState().rocketChat.rooms) {
        resolve(getState().rocketChat.rooms[user]);
      } else {
        resolve(dispatch(createDirectMessage(user)));
      }
    })
  };
}

/**
 * Loads history for the room
 * @param {string} room - room identification
 * @param {int} quantity - default 10, the message quantity
 * @param {Date|null} lastOnline - default null, the date of the last time the client got data for the room
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function loadHistory(room, quantity = RC_MESSAGE_NUMBER, lastOnline = null) {
  return (dispatch) => {
    dispatch(
      {
        type: RC_METHOD,
        payload: {
          method: RC_METHOD_HISTORY,
          params: [
            room,
            // The oldest message timestamp date (or null) - this is used to do pagination
            null,
            quantity,
            lastOnline
          ]
        }
      }
    );
  };
}

/**
 * Gets subscriptions
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function getSubscriptions() {
  return (dispatch) => {
    dispatch(
      {
        type: RC_METHOD,
        payload: {
          method: RC_METHOD_SUBSCRIPTIONS,
          params: []
        }
      }
    );
  }
}

/**
 * Gets history for the user
 * @param {string} user - username
 * @param {int} quantity - default 10
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function getHistory(user, quantity = RC_MESSAGE_NUMBER) {
  return (dispatch, getState) => {
    return dispatch(getUserRoom(user))
      .then((room) => {
        const {rocketChat} = getState();
        const {rid} = room;

        return rid in rocketChat.messages
          ? getState().rocketChat.messages.rid
          : dispatch(loadHistory(rid, quantity));
      })
  };
}

/**
 * Sends direct message to a user
 * @param {string} user - username
 * @param {string} message - direct message
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function sendDirectMessage(user, message) {
  return (dispatch) => {
    return dispatch(getUserRoom(user))
      .then((room) => {
        dispatch(sendMessage(room.rid, message));
      })
  };
}

/**
 * Sets history as being read
 * @param {string} user - username
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function setHistoryRead(user) {
  return (dispatch) => {
    dispatch(getUserRoom(user))
      .then((room) => {
        dispatch(
          {
            type: RC_METHOD,
            payload: {
              method: RC_METHOD_READ_MESSAGES,
              params: [room.rid]
            }
          }
        );
      });
  };
}

/**
 * Gets call action
 * @param {string} room - room identification
 * @param {string} action - name, default is empty
 * @returns {string} call action
 */
function getCallAction(room, action = '') {
  return TW_VIDEO_TAG + room + (action.length ? TW_ACTION_SEPARATOR + action : '');
}

/**
 * Sends invitation for a video call
 * @param {string} user - username
 * @param {string} room - room name
 * @returns {function(dispatch): Promise<string>} promised thunk to be dispatched -> room
 */
export function inviteCall(user, room) {
  return (dispatch) => {
    return new Promise((resolve) => {
      dispatch(
        sendDirectMessage(user, getCallAction(room))
      );
      resolve(room)
    });
  }
}

/**
 * Stops a call
 * @param {string} user - username
 * @param {string} room - room name
 * @returns {function(dispatch): Promise<string>} promised thunk to be dispatched -> room
 */
export function stopCall(user, room) {
  return (dispatch) => {
    return new Promise((resolve) => {
      dispatch(sendDirectMessage(user, getCallAction(room, TW_ACTION_STOP)))
        .then(() => {
          const userModel = dispatch(getSenior(getSeniorId(user)));

          dispatch(addNotification(
            __("The call with %user% has ended.")
              .replace('%user%', `${userModel.firstname} ${userModel.lastname}`),
            NOTIFICATION_WARNING
          ));
        });
      resolve(room);
    });
  }
}

/**
 * Declines pending call
 * @param {string} user - username
 * @param {string} room - room name
 * @returns {function(dispatch): Promise<string>} promised thunk to be dispatched -> room
 */
export function declineCall(user, room) {
  return (dispatch) => {
    return new Promise((resolve) => {
      dispatch(
        sendDirectMessage(user, getCallAction(room, TW_ACTION_DECLINE))
      );
      resolve(room)
    });
  }
}

/**
 * Cancels pending call from organizer side
 * @param {string} user - username
 * @param {string} room - room name
 * @returns {function(dispatch): Promise<string>} promised thunk to be dispatched -> room
 */
export function cancelCall(user, room) {
  return (dispatch) => {
    return new Promise((resolve) => {
      dispatch(
        sendDirectMessage(user, getCallAction(room, TW_ACTION_CANCEL))
      );
      resolve(room)
    });
  }
}

/**
 * Sends typing notification to a room
 * NB! Just ones in MESSAGE_TYPING_WAIT
 * @param {string} user user name who is typing
 * @returns {function(dispatch, getState): Promise<any>} promised thunk to be dispatched
 */
export function typing(user) {
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      // (kkashpur) For now we do NOT send typing
      if (!getState().rocketChat.isTyping && false) {
        setTimeout(() => {
          dispatch(
            {
              type: RC_TYPING_SEND,
              payload: false
            }
          );
        }, MESSAGE_TYPING_WAIT);
        dispatch(
          {
            type: RC_TYPING_SEND,
            payload: true
          }
        );
        dispatch(getUserRoom(user))
          .then((room) => {
            dispatch(
              {
                type: RC_METHOD,
                payload: {
                  method: RC_NOTIFY_ROOM,
                  params: [
                    `${room.rid}${RC_SUBSCRIPTION_TYPING}`,
                    getState().rocketChat.user.username,
                    true
                  ]
                }
              }
            );
          });
      }
      resolve();
    });
  }
}

/**
 * Gets notification by it`s index
 * @param {int} index notification index
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function getNotification(index) {
  return (dispatch, getState) => {
    return getState().rocketChat.notifications[index];
  }
}

/**
 * Marks messages as read/remove message from message list
 * @param {int} index message index
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function checkNotification(index) {
  return (dispatch) => {
    return new Promise((resolve) => {
      resolve(
        dispatch(
          {
            type: RC_NOTIFICATION_CHECK,
            index: index
          }
        )
      );
    });
  };
}

/**
 * Removes room
 * @param {string} user room identification of user video call
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function removeRoom(user) {
  return (dispatch) => {
    return new Promise((resolve) => {
      resolve(
        dispatch(
          {
            type: RC_ROOM_REMOVE,
            user: user
          }
        )
      );
    });
  };
}

/**
 * Login action
 * @param {string} email user email
 * @param {string} secret user secret
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function loginAction(email, secret) {
  return (dispatch) => {
    return dispatch(
      once(
        (state) => state.rocketChat.isConnected,
        () => {
          return {
            type: RC_METHOD,
            payload: {
              method: RC_METHOD_LOGIN,
              params: [
                {
                  user: {email: email},
                  password: {
                    digest: sjcl.codec.hex.fromBits(sjcl.hash.sha256.hash(secret)),
                    algorithm: "sha-256"
                  }
                }
              ]
            }
          }
        }
      )
    )
  }
}

/**
 * Gets user roles
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function getUserRoles() {
  return (dispatch) => {
    return dispatch(
      {
        type: RC_METHOD,
        payload: {
          method: RC_METHOD_USER_ROLES,
          params: []
        }
      }
    );
  }
}

/**
 * Login to Rocket.Chat
 * Done with the connection
 * @param {string} email user email
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function login(email) {
  return (dispatch, getState) => {
    if (auth.is() && !getState().rocketChat.isConnected) {
      getSecret()
        .then((secret) => {
          dispatch(connect(auth.getToken()));
          dispatch(loginAction(email, secret));
        });
    }
  }
}

/**
 * Loads history difference
 * @param {object} room - room object
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function loadHistoryDiff(room) {
  return (dispatch, getState) => {
    const {messages} = getState().rocketChat;

    if (room.unread > 0) {
      dispatch(loadHistory(room.rid, room.unread));
    } else if (room.rid in messages) {
      const [message] = messages[room.rid];

      dispatch(loadHistory(room.rid, 1, message.ts));
    }
  }
}

/**
 * Sends offline messages
 * @param {object} room - room object
 * @returns {function(dispatch)} thunk to be dispatched
 */
export function sendOfflineMessages(room) {
  return (dispatch) => {
    const messages = getStoredMessages();

    messages.forEach((message) => {
      if (room.rid === message.rid) {
        dispatch(sendMessage(message.rid, message.msg));
      }
    }, []);
  }
}

/**
 * Registers a user to Rocket.Chat
 * @returns {function(dispatch): Promise<any>} promised thunk to be dispatched
 */
export function register() {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const {user} = getState();

      getSecret()
        .then((secret) => {
          requestAuth()
            .post(`https://${ROCKET_CHAT_DOMAIN}/api/v1/users.register`)
            .send(
              {
                email: user.email,
                pass: secret,
                name: user.name,
                username: RC_USER_PREFIX_FP + user.cust_id
              }
            )
            .then(() => {
              dispatch({type: RC_REGISTER});
              resolve(
                dispatch(
                  loginAction(
                    user.email,
                    secret
                  )
                )
              );
            })
            .catch(reject);
        });
    });
  }
}

/**
 * Check connection state
 * If it is not connected - RC_DISCONNECT will be invoke and try to login
 * It will be triggered every RC_CHECK_STATE_STEP ms
 * RC_CHECK_STATE_STEP must be greater than RC ping-pong strategy
 * @returns {function(dispatch, getState)} thunk to be dispatched
 */
export function checkState() {
  return (dispatch, getState) => {
    if (!isCheckState) {
      isCheckState = true;
      setInterval(() => {
        if (getState().rocketChat.isChecked) {
          dispatch(
            {
              type: RC_CHECK_STATE,
              payload: false
            }
          );
        } else {
          dispatch(reconnect());
        }
      }, RC_CHECK_STATE_STEP);
    }
  }
}

export function reconnect() {
  return (dispatch, getState) => {
    const {user, rocketChat} = getState();

    if (rocketChat.isConnected) {
      dispatch({type: RC_DISCONNECT});
    }
    dispatch(login(user.email))
  }
}

async function restLogin() {
  const { body } = await requestAuth().post(PROFILE_URL);
  const secret = await getSecret();

  return request.post(ROCKET_CHAT_LOGIN_URL).send({ user: body.email, password: secret });
}

export async function getMessagesFor(roomId) {
  const { body } = await restLogin();

  const response = await request.get(ROCKET_CHAT_HISTORY_URL)
    .set('X-Auth-Token', body.data.authToken)
    .set('X-User-Id', body.data.userId)
    .query({ roomId, count: 50 });

  return response.body;
}

export function addMessages(messages) {
  return (dispatch, getState) => dispatch({
    type: RC_MESSAGE_ALL,
    user: getState().rocketChat.user,
    unreadNotLoaded: 0,
    messages
  })
}